import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';

import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
// Import webhook debugging utility
import Footer from './components/Footer';
// Import new provider architecture
import { AppProviders } from './contexts/AppProviders';
import { useJWT } from './contexts/JWTProvider';
import Approve from './pages/Approve';
import Confirmation from './pages/Confirmation';
import DirectOnboard from './pages/DirectOnboard';
import ErrorPage from './pages/ErrorPage';
import MissingELD from './pages/MissingELD';
import NotFoundPage from './pages/NotFoundPage';
import Onboard from './pages/Onboard';
import SelectTSP from './pages/SelectTSP';
import SkipCompletedPage from './pages/SkipCompletedPage';
import SuccessPage from './pages/SuccessPage';
import TestErrorPage from './pages/TestErrorPage';
import UpdatePermissions from './pages/UpdatePermissions';
import TSPConfig from './pages/tspConfig';
import { defaultTheme } from './themes';
import { generateOAuthRedirectRoutes } from './tsps';
import { validateThemeInDev } from './utils/ThemeValidator';
import './utils/debugLogs';
// Import debug utilities
import './utils/webhookDebugger';

// Renamed from App to AppContent
function AppContent() {
  const jwt = useJWT(); // Use JWT provider for theme

  // Generate OAuth redirect routes dynamically
  const oauthRedirectRoutes = generateOAuthRedirectRoutes();

  const muiTheme = createTheme(jwt.theme || defaultTheme); // Use JWT theme

  // Validate the theme in development mode
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      validateThemeInDev(jwt.theme || defaultTheme);
    }
  }, [jwt.theme]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            // Background only on desktop
            '@media (min-width: 600px)': {
              backgroundImage: jwt.theme?.custom?.backgroundImage || 'none',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundAttachment: 'fixed',
              backgroundSize: 'cover',
            },
            // Mobile: clean background
            '@media (max-width: 599px)': {
              backgroundColor: muiTheme.palette?.background?.paper || '#ffffff',
            },
            height: '100vh',
            margin: 0,
            fontFamily: muiTheme.typography?.fontFamily,
            color: muiTheme.palette?.text?.primary,
            // Mobile optimization
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            // Prevent zoom on input focus for iOS
            fontSize: '16px',
            overflow: 'hidden', // Prevent body scroll
          },
          '#root': {
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            // Support for mobile safe areas
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            paddingTop: 'env(safe-area-inset-top)',
            overflow: 'hidden', // Prevent root scroll
          },
          // Ensure proper touch targets on mobile
          '@media (hover: none) and (pointer: coarse)': {
            'button, [role="button"], input[type="submit"], input[type="button"]':
              {
                minHeight: '44px',
                minWidth: '44px',
              },
          },
          // Improve form input behavior on mobile
          'input[type="text"], input[type="email"], input[type="password"], input[type="tel"], input[type="url"], textarea':
            {
              fontSize: '16px !important', // Prevent zoom on iOS
              borderRadius: '4px',
            },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          // Add bottom padding to account for footer height (only on desktop)
          paddingBottom: {
            xs: 0, // No footer on mobile
            sm: '80px', // Desktop footer height
          },
        }}
      >
        <Grid
          container
          justifyContent='center'
          alignItems='flex-start'
          sx={{
            minWidth: '100%',
            height: '100%',
            display: 'flex',
            background: 'transparent',
            flex: 1,
          }}
        >
          {/* Use consistent modern design for all pages */}
          <Routes>
            <Route path='/' element={<Approve />} />
            <Route path='/login/:tsp' element={<Approve />} />
            <Route path='/direct/:tsp' element={<DirectOnboard />} />
            <Route path='/skipLinking' element={<MissingELD />} />
            <Route path='/missingELD' element={<MissingELD />} />
            <Route path='/selectTSP' element={<SelectTSP />} />
            <Route path='/onboard' element={<Onboard />} />
            <Route path='/updateOrAdd' element={<UpdatePermissions />} />
            <Route path='/config/:tspKey' element={<TSPConfig />} />
            <Route path='/config' element={<TSPConfig />} />
            <Route path='/error' element={<ErrorPage />} />
            <Route path='/success' element={<SuccessPage />} />
            <Route path='/skip-completed' element={<SkipCompletedPage />} />
            <Route path='/confirmation' element={<Confirmation />} />
            {oauthRedirectRoutes.map((route) => (
              <Route
                key={route.key}
                path={route.path}
                element={route.element}
              />
            ))}
            {process.env.NODE_ENV === 'development' && (
              <Route path='/dev/test-error' element={<TestErrorPage />} />
            )}
            {/* Catch-all route for unmatched paths */}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
          <Footer />
        </Grid>
      </Box>
    </ThemeProvider>
  );
}

// New App component that sets up the new provider architecture
function App() {
  return (
    <ErrorBoundary>
      <AppProviders>
        <AppContent />
      </AppProviders>
    </ErrorBoundary>
  );
}

export default App;
